import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Monitor, Smartphone, ArrowLeft } from "lucide-react";
import { findMobileTitle } from "../../lib/mobileRoutes";
import { useNavigationHistory } from "../../contexts/NavigationHistoryContext";

/**
 * Full-screen placeholder shown on mobile viewports for pages that only
 * make sense on a large screen (Reports, Dispatch Board, Marketing, etc.).
 */
function DesktopOnlyScreen({ title }) {
  const { goBack } = useNavigationHistory();
  return (
    <div
      className="mv md:hidden fixed inset-0 z-30 flex flex-col items-center justify-center px-6 text-center"
      style={{
        backgroundColor: "#ffffff",
        color: "#0f172a",
        paddingTop: "calc(3.5rem + env(safe-area-inset-top))",
        paddingBottom: "calc(5rem + env(safe-area-inset-bottom))",
      }}
      data-testid="desktop-only-screen"
    >
      <div className="h-20 w-20 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: "#EFF6FF" }}>
        <Monitor className="h-10 w-10" color="#2563EB" strokeWidth={1.75} />
      </div>
      <div className="text-[22px] font-bold mb-2" style={{ color: "#0B1E3F" }}>
        Please use a desktop
      </div>
      {title && (
        <div className="text-[13.5px] font-semibold uppercase tracking-wide mb-3" style={{ color: "#64748b" }}>
          {title}
        </div>
      )}
      <p className="text-[15px] leading-relaxed max-w-xs mb-6" style={{ color: "#475569" }}>
        This page is built for a larger screen and isn&apos;t available on mobile. Open it on your desktop to keep going.
      </p>
      <button
        type="button"
        onClick={() => goBack("/dashboard")}
        data-testid="desktop-only-back"
        className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full text-[14.5px] font-bold active:opacity-90"
        style={{ backgroundColor: "#2563EB", color: "#ffffff" }}
      >
        <ArrowLeft className="h-4 w-4" /> Back to Dashboard
      </button>
    </div>
  );
}

/**
 * Placeholder shown on desktop/tablet (>= md) for surfaces that only make
 * sense on a phone (the "Mobile Tech" shell). Mirrors DesktopOnlyScreen but
 * for the opposite direction, so reaching /mobile from a large screen never
 * shows a blank page.
 */
function MobileOnlyScreen() {
  return (
    <div
      className="hidden md:flex fixed inset-0 z-30 flex-col items-center justify-center px-6 text-center"
      style={{ backgroundColor: "#ffffff", color: "#0f172a" }}
      data-testid="mobile-only-screen"
    >
      <div className="h-20 w-20 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: "#EFF6FF" }}>
        <Smartphone className="h-10 w-10" color="#2563EB" strokeWidth={1.75} />
      </div>
      <div className="text-[22px] font-bold mb-2" style={{ color: "#0B1E3F" }}>
        Please use a mobile device
      </div>
      <p className="text-[15px] leading-relaxed max-w-xs mb-6" style={{ color: "#475569" }}>
        This is the mobile technician view. Open it on your phone, or head to Jobs to keep working on this screen.
      </p>
      <Link
        to="/jobs"
        data-testid="mobile-only-back"
        className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full text-[14.5px] font-bold hover:opacity-90"
        style={{ backgroundColor: "#2563EB", color: "#ffffff" }}
      >
        <ArrowLeft className="h-4 w-4" /> Go to Jobs
      </Link>
    </div>
  );
}

/**
 * useIsMobile — reactive < md viewport detector.
 */
export function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < breakpoint
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

/**
 * Wraps a route so it renders the desktop-only placeholder on mobile
 * and the underlying page on desktop.
 */
export function DesktopOnly({ pathname, children }) {
  const isMobile = useIsMobile();
  if (isMobile) return <DesktopOnlyScreen title={findMobileTitle(pathname)} />;
  return children;
}

/**
 * Wraps a route so it renders the mobile-only placeholder on desktop/tablet
 * and the underlying mobile shell on phones.
 */
export function MobileOnly({ children }) {
  const isMobile = useIsMobile();
  if (!isMobile) return <MobileOnlyScreen />;
  return children;
}

/**
 * Picks between two elements based on viewport width. Use for pages that
 * have a dedicated mobile redesign.
 */
export function ResponsivePage({ mobile, desktop }) {
  const isMobile = useIsMobile();
  return isMobile ? mobile : desktop;
}

export default DesktopOnlyScreen;
